import { useMemo, useRef } from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { Icon } from '@/common/components/Icon'
import { Card } from '@/components/ui'
import { cn } from '@/lib/utils'

interface SalesDataPoint {
  label: string
  revenue: number
}

interface SalesGraphicWidgetProps {
  className?: string
  data?: SalesDataPoint[]
  totalSales?: string
  subtitle?: string
  currencySymbol?: string
  soldTicketsCount?: number
}

const MIN_PX_PER_POINT = 60

const IconButton: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex size-11 relative items-center justify-center rounded-3xl border border-white/20">
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1/2 h-1/2 bg-accent-1/40 rounded-3xl blur-sm" />
    {children}
  </div>
)

const formatYAxis = (value: number) => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}k`
  return `${value}`
}

const CustomTooltip = ({
  active,
  payload,
  currencySymbol = '€',
}: {
  active?: boolean
  payload?: Array<{ value: number; dataKey: string }>
  currencySymbol?: string
}) => {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-xl border border-monochrome-120 bg-monochrome-4-60 px-3 py-2 backdrop-blur-md">
      <p className="text-body-medium text-monochrome-2">
        Revenue:{' '}
        <span className="text-monochrome-1">
          {currencySymbol}
          {payload[0]?.value?.toLocaleString()}
        </span>
      </p>
      {payload[1] && (
        <p className="text-body-medium text-monochrome-2">
          <span className="text-monochrome-1">
            {payload[1]?.value?.toLocaleString()}
          </span>
        </p>
      )}
    </div>
  )
}

const CustomXAxisTick = ({
  x,
  y,
  payload,
  index,
  dataLength,
}: {
  x?: number
  y?: number
  payload?: { value: string }
  index?: number
  dataLength: number
}) => {
  const isLast = index === dataLength - 1
  const dotSize = isLast ? 10 : 6
  const dotColor = isLast ? '#06FF48' : 'rgba(169,169,169,0.4)'

  return (
    <g transform={`translate(${x},${y})`}>
      <circle
        cx={0}
        cy={8}
        r={dotSize / 2}
        fill={dotColor}
        {...(isLast && { filter: 'drop-shadow(0 0 8px hsl(136,100%,51%))' })}
      />
      <text
        x={0}
        y={26}
        textAnchor="middle"
        fill={isLast ? '#FFFFFF' : '#A9A9A9'}
        fontSize={13}
        fontWeight={500}
      >
        {payload?.value}
      </text>
    </g>
  )
}

export const SalesGraphicWidget: React.FC<SalesGraphicWidgetProps> = ({
  className,
  data,
  totalSales,
  subtitle = 'Last 6 Months',
  currencySymbol = '€',
  soldTicketsCount = 0,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null)

  const minChartWidth = useMemo(() => {
    if (!data?.length) return 0
    return data.length * MIN_PX_PER_POINT
  }, [data])

  const dataLength = data?.length ?? 0

  return (
    <Card
      className={cn(
        'relative flex flex-col overflow-hidden border-2 border-white/10 bg-black/60 p-[30px] backdrop-blur-sm',
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <IconButton>
            <Icon
              icon="calendar-2"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              className="text-accent-1"
            />
          </IconButton>
          <div className="flex flex-col">
            <span className="text-title-xxs text-monochrome-1">Sales</span>
            <span className="text-body-medium text-monochrome-2">
              {subtitle}
            </span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-[3px]">
          <span className="text-title-xs text-monochrome-1">{totalSales}</span>
          <span className="text-body-medium text-monochrome-2">
            Total {soldTicketsCount} Sales
          </span>
        </div>
      </div>

      {/* Chart */}
      <div ref={scrollRef} className="mt-4 flex-1 overflow-x-auto">
        <div style={{ minWidth: minChartWidth }}>
          <ResponsiveContainer width="100%" height={320}>
            <AreaChart
              data={data}
              margin={{ top: 10, right: 20, left: -10, bottom: 30 }}
            >
              <defs>
                <linearGradient
                  id="revenueGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor="#06FF48" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#06FF48" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="0"
                stroke="rgba(255,255,255,0.05)"
                vertical={false}
              />
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                tick={(props) => (
                  <CustomXAxisTick
                    {...props}
                    x={props.x as number}
                    y={props.y as number}
                    dataLength={dataLength}
                  />
                )}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tickFormatter={formatYAxis}
                tick={{ fill: '#A9A9A9', fontSize: 13, fontWeight: 500 }}
                dx={-5}
              />
              <Tooltip
                content={<CustomTooltip currencySymbol={currencySymbol} />}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#06FF48"
                strokeWidth={2.5}
                fill="url(#revenueGradient)"
                dot={false}
                activeDot={{
                  r: 5,
                  fill: '#0a0c0f',
                  stroke: '#06FF48',
                  strokeWidth: 2,
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </Card>
  )
}
